import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User as FirebaseUser } from 'firebase/auth';
import { onAuthStateChange, signOutUser, signInWithGoogle } from '../firebase';

// カスタムUser型（My Routineと同じ）
interface User {
  id: string;
  username: string;
  email: string;
  createdAt: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  loginWithGoogle: () => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<{ success: boolean; error?: string }>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  token: null,
  loginWithGoogle: async () => ({ success: false }),
  logout: async () => ({ success: false }),
  loading: true,
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    
    const unsubscribe = onAuthStateChange(async (firebaseUser: FirebaseUser | null) => {
      if (!isMounted) return;
      
      console.log('Auth state changed:', firebaseUser ? 'User logged in' : 'User logged out');
      
      try {
        if (firebaseUser) {
          console.log('Firebase user:', {
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            displayName: firebaseUser.displayName,
            emailVerified: firebaseUser.emailVerified
          });

          // Get the ID token with extended timeout
          const tokenPromise = firebaseUser.getIdToken(true); // force refresh
          const timeoutPromise = new Promise((_, reject) => {
            setTimeout(() => reject(new Error('Token fetch timeout')), 10000); // 10秒に延長
          });
          
          const idToken = await Promise.race([tokenPromise, timeoutPromise]) as string;
          console.log('Token acquired successfully');
          
          // Convert Firebase user to our User interface
          const userData: User = {
            id: firebaseUser.uid,
            username: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'User',
            email: firebaseUser.email || '',
            createdAt: firebaseUser.metadata.creationTime || new Date().toISOString()
          };
          
          if (isMounted) {
            setUser(userData);
            setToken(idToken);
            setLoading(false);
            console.log('User state updated successfully');
          }
        } else {
          console.log('No Firebase user, clearing state');
          if (isMounted) {
            setUser(null);
            setToken(null);
            setLoading(false);
          }
        }
      } catch (error) {
        console.error('Auth state change error:', error);
        if (isMounted) {
          setUser(null);
          setToken(null);
          setLoading(false);
        }
      }
    });

    // タイムアウト処理（5秒後に強制的にloadingをfalseにする）
    const timeoutTimer = setTimeout(() => {
      if (isMounted) {
        setLoading(false);
      }
    }, 5000);

    return () => {
      isMounted = false;
      clearTimeout(timeoutTimer);
      unsubscribe();
    };
  }, []);



  const loginWithGoogle = async (): Promise<{ success: boolean; error?: string }> => {
    try {
      console.log('Attempting Google login');
      const userCredential = await signInWithGoogle();
      console.log('Google login successful:', userCredential.uid);
      // Firebaseの認証状態管理に任せるため、手動でのsetTokenは削除
      return { success: true };
    } catch (error: any) {
      console.error('Google login error:', {
        code: error.code,
        message: error.message,
        stack: error.stack
      });
      let errorMessage = 'Googleログインに失敗しました';
      
      switch (error.code) {
        case 'auth/popup-closed-by-user':
          errorMessage = 'ログインがキャンセルされました';
          break;
        case 'auth/popup-blocked':
          errorMessage = 'ポップアップがブロックされました。ポップアップを許可してください';
          break;
        case 'auth/cancelled-popup-request':
          errorMessage = 'ログインがキャンセルされました';
          break;
        case 'auth/account-exists-with-different-credential':
          errorMessage = 'このメールアドレスは別の方法で登録されています';
          break;
        case 'auth/network-request-failed':
          errorMessage = 'ネットワークエラーが発生しました';
          break;
        default:
          errorMessage = `Googleログインエラー: ${error.message}`;
      }
      
      return { success: false, error: errorMessage };
    }
  };

  const logout = async (): Promise<{ success: boolean; error?: string }> => {
    try {
      await signOutUser();
      setUser(null);
      setToken(null);
      return { success: true };
    } catch (error: any) {
      return { success: false, error: 'ログアウトに失敗しました' };
    }
  };

  const value: AuthContextType = {
    user,
    token,
    loginWithGoogle,
    logout,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 